/**
 * Authentication helpers — token + per-user state isolation.
 *
 * Strategy:
 *   - Sign out only removes the auth token. User-scoped state (saved
 *     portfolios, search history, watchlists, etc.) stays in
 *     localStorage so if the SAME user signs back in, their work
 *     persists.
 *   - On login, we compare the new user's identity (from the JWT) to
 *     the last user stored in `alphagrade_last_user`. If they differ,
 *     we wipe all user-scoped state before storing the new token.
 *     This guarantees no data bleed between accounts on the same
 *     device.
 */

export const TOKEN_KEY = 'alphagrade_auth_token';
export const LAST_USER_KEY = 'alphagrade_last_user';

/**
 * Keys that hold user-scoped data. Wiped only when a DIFFERENT user
 * logs in on the same device. Not touched on sign-out.
 *
 * Keys explicitly NOT included (device-level, never wiped):
 *   - alphagrade_install_prompt_dismissed (device preference)
 *   - alphagrade_pwa_installed (device fact)
 *   - alphagrade_onboarding_complete (per-device first-run flag)
 *   - theme / colorScheme (device preference)
 */
const USER_SCOPED_KEYS = [
  TOKEN_KEY,
  LAST_USER_KEY,
  'alphagrade_saved_portfolios',
  'alphagrade_score_history',
  'alphagrade_portfolio_items',
  'alphagrade_analysis_result',
  'alphagrade_watchlist',
  'alphagrade_watchlists',
  'alphagrade_price_alerts',
  'screener_search_history',
  // 2026-09-11: screener_last_input was missing here, so the previous user's
  // ticker survived a different user signing in on the same device and the
  // screener came up pre-loaded with it. Found while testing a fresh free
  // account. Anything the app persists per user belongs on this list.
  'screener_last_input',
  'screener_presets',
];

/**
 * Decode a JWT payload (no signature verification — server validates
 * on every request). Returns null on any parse error.
 */
function decodeJwtPayload(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const padded = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(padded + '==='.slice((padded.length + 3) % 4));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Extract the user identity (email) from a JWT.
 */
export function getUserFromToken(token) {
  const payload = decodeJwtPayload(token);
  return payload?.sub || null;
}

/**
 * Wipe ALL user-scoped state. Used when a different user logs in.
 */
export function clearUserState() {
  USER_SCOPED_KEYS.forEach((key) => localStorage.removeItem(key));
}

/**
 * Compare a newly issued token's user identity to the previously
 * stored one. If they differ, wipe state. Either way, store the new
 * identity. Returns true if state was wiped (different user).
 */
export function reconcileUserOnLogin(newToken) {
  const newUser = getUserFromToken(newToken);
  const lastUser = localStorage.getItem(LAST_USER_KEY);

  const isDifferentUser = !lastUser || lastUser !== newUser;
  if (isDifferentUser) {
    clearUserState();
  }

  if (newUser) {
    localStorage.setItem(LAST_USER_KEY, newUser);
  }
  return isDifferentUser;
}

/**
 * Sign out: only remove the auth token. User-scoped state stays in
 * localStorage so the same user can resume their work next time. If
 * a different user signs in on this device, reconcileUserOnLogin
 * will wipe it then.
 */
export function signOut() {
  localStorage.removeItem(TOKEN_KEY);
  window.location.href = '/';
}

/**
 * True if a token is present. UI-level check for adapting the app to
 * logged-out visitors (the server still validates every request).
 */
export function isAuthenticated() {
  return !!localStorage.getItem(TOKEN_KEY);
}
